import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { lazy, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import { LoadingState, MarkRequestReadState, ReadState as RenderableReadState } from '@atlassian/notification-types/types';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import { triggerConfluenceCommentReplyButtonClicked, triggerConfluenceCommentReplyCreated, triggerDocumentCollapsed, triggerDocumentExpanded, triggerJiraCommentReplyButtonClicked, triggerJiraCommentReplyCancelled, triggerJiraCommentReplyCreated, triggerKeyboardShortcutUsed, triggerMarkOneReadButtonClicked, triggerNotificationLinkClicked, triggerNotificationViewedTrackEvent, triggerRequestNotificationViewed, useCreateFireAnalyticsFromTrigger } from '../../utils/analytics';
import { LinkType } from '../../utils/analytics/types';
import { ExperienceMark, ExperienceSuccess, NotificationExperienceMarkKeys } from '../../utils/experience-tracking/adapter';
import { Experiences } from '../../utils/experience-tracking/types';
import { getUrlWithAtlOrigin } from '../../utils/getUrlWithAtlOrigin';
import { isDesNotification } from '../../utils/is-des-notification';
import { isRequestNotification } from '../../utils/is-request-notification';
import { useFocus } from '../../utils/useFocus';
import { keyboardEventToAction, useFocusManager } from '../../utils/useFocusManager';
import { KeyboardActionToAnalyticSubject } from '../../utils/useFocusManager/types';
import ViewedTracker from '../../utils/viewed-tracker';
import { AvatarSkeleton } from '../avatar-layout';
import { AvatarThread, PositionInGroup } from '../avatar-thread';
import ErrorBoundary from '../error-boundary';
import { ErrorBoundarySuspense } from '../error-boundary-suspense';
import FormattedText, { useFormattedTextValues } from '../formatted-text';
import { NotificationsStoreContext, useOriginId } from '../notifications-context/main';
import AdditionalCtaButtons from './additional-cta-buttons';
import AtlassianAvatarContainer from './atlassian-avatar-container';
import AvatarContainer from './avatar-container';
import Document from './document';
import Entity from './entity';
import { JIRA_REACT_BAR_EVENTS, JiraReactBar } from './jira-react-bar/main';
import JiraReactReplyBar from './jira-react-reply-bar/main';
import MessageWithTimestamp from './message-with-timestamp';
import Path from './path';
import ReactReplyBar, { CONFLUENCE_REACT_REPLY_BAR_EVENTS } from './react-reply-bar/main';
import ReadStateDropdownMenu from './read-state-dropdown-menu';
import ReadStateIndicator from './read-state-indicator';
import { BackgroundFadeOut, DocumentLeftSpacing, EnableMouseEventsDocumentWrapper, EnableMouseEventsProfileWrapper, EnableMouseEventsReactReplyWrapper, FlexRow, FullWidth, InboxReadStateIndicatorContainer, ItemContainer, MainAction, NotificationContainer, SummaryContainer } from './styled';
import SuccessFlags from './success-flags';
import { buildPathText, generateCommentSessionId, getMainActionUrl, getPathUrl, selectUnit } from './utils';
var LazyCommentEditor = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "comment-editor-main" */
  './comment-editor/main');
});
var LazyJiraCommentEditor = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: "jira-comment-editor-main" */
  './jira-react-reply-bar/jira-comment-editor/main');
});
export { BackgroundFadeOut } from './styled';
export function NotificationItem(_ref) {
  var _notification$content, _notification$content2, _notification$content3, _notification$analyti, _notification$content4, _notification$content5, _notification$content6, _notification$content7, _notification$content8;
  var notification = _ref.notification,
    forceContentLoading = _ref.forceContentLoading,
    listIndex = _ref.listIndex,
    markBulkNotifications = _ref.markBulkNotifications,
    className = _ref.className,
    positionInGroup = _ref.positionInGroup,
    _ref$expandGroupButto = _ref.expandGroupButton,
    expandGroupButton = _ref$expandGroupButto === void 0 ? null : _ref$expandGroupButto,
    displayReadStateContextMenu = _ref.displayReadStateContextMenu,
    _ref$onGroupReadState = _ref.onGroupReadStateChange,
    onGroupReadStateChange = _ref$onGroupReadState === void 0 ? function () {} : _ref$onGroupReadState,
    _ref$groupReadStates = _ref.groupReadStates,
    groupReadStates = _ref$groupReadStates === void 0 ? [] : _ref$groupReadStates,
    onClick = _ref.onClick,
    _ref$readStateIndicat = _ref.readStateIndicatorStyle,
    readStateIndicatorStyle = _ref$readStateIndicat === void 0 ? 'standard' : _ref$readStateIndicat,
    emojiProvider = _ref.emojiProvider,
    mentionProvider = _ref.mentionProvider,
    onCommentCreatedFn = _ref.onCommentCreatedFn,
    isEligibleForBellNotifRepliesCtxExp = _ref.isEligibleForBellNotifRepliesCtxExp,
    onUnsavedCommentDraft = _ref.onUnsavedCommentDraft;
  var intl = useIntl();
  var originId = useOriginId();
  var isRequestNotificationType = isRequestNotification(notification);
  var isDesNotificationType = isDesNotification(notification);
  var showAdditionalCtaButtons = isRequestNotificationType || ((_notification$content = notification.content) === null || _notification$content === void 0 ? void 0 : _notification$content.actions) && ((_notification$content2 = notification.content) === null || _notification$content2 === void 0 ? void 0 : _notification$content2.actions.length) >= 2 && ((_notification$content3 = notification.content) === null || _notification$content3 === void 0 ? void 0 : _notification$content3.actions.length) <= 3;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    hasUserInteracted = _useState4[0],
    setHasUserInteracted = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    markAsReadActive = _useState6[0],
    setMarkAsReadActive = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    containerId = _useState8[0],
    setContainerId = _useState8[1];
  var _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    containerType = _useState0[0],
    setContainerType = _useState0[1];
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    isInlineComment = _useState10[0],
    setIsInlineComment = _useState10[1];
  var _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    replyCommentParentId = _useState12[0],
    setReplyCommentParentId = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    showCommentEditor = _useState14[0],
    setShowCommentEditor = _useState14[1];
  var _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    showCommentThreadLink = _useState16[0],
    setShowCommentThreadLink = _useState16[1];
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    currentUserIsWatching = _useState18[0],
    setCurrentUserIsWatching = _useState18[1];
  var _useState19 = useState({}),
    _useState20 = _slicedToArray(_useState19, 2),
    jiraReplyAnalyticsPayload = _useState20[0],
    setJiraReplyAnalyticsPayload = _useState20[1];
  var _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    showJiraCommentEditor = _useState22[0],
    setShowJiraCommentEditor = _useState22[1];
  var _useState23 = useState([]),
    _useState24 = _slicedToArray(_useState23, 2),
    flags = _useState24[0],
    setFlags = _useState24[1];
  var subProduct = notification === null || notification === void 0 || (_notification$analyti = notification.analyticsAttributes) === null || _notification$analyti === void 0 ? void 0 : _notification$analyti.subProduct;
  var inboxStyleReadStateIndicatorRef = useRef(null);
  var profileWrapperRef = useRef(null);
  var fireRequestNotificationViewed = useCreateFireAnalyticsFromTrigger(triggerRequestNotificationViewed);
  var fireTriggerKeyboardShortcutUsed = useCreateFireAnalyticsFromTrigger(triggerKeyboardShortcutUsed);
  var fireNotificationLinkClicked = useCreateFireAnalyticsFromTrigger(triggerNotificationLinkClicked);
  var fireJiraCommentReplyClick = useCreateFireAnalyticsFromTrigger(triggerJiraCommentReplyButtonClicked);
  var fireJiraCommentReplyCreated = useCreateFireAnalyticsFromTrigger(triggerJiraCommentReplyCreated);
  var fireJiraCommentReplyCancelled = useCreateFireAnalyticsFromTrigger(triggerJiraCommentReplyCancelled);
  var fireMarkOneReadClicked = useCreateFireAnalyticsFromTrigger(triggerMarkOneReadButtonClicked);
  var fireTriggerDocumentExpanded = useCreateFireAnalyticsFromTrigger(triggerDocumentExpanded);
  var fireTriggerDocumentCollapsed = useCreateFireAnalyticsFromTrigger(triggerDocumentCollapsed);
  var fireNotificationViewedTrackEvent = useCreateFireAnalyticsFromTrigger(triggerNotificationViewedTrackEvent);
  var fireConfluenceCommentReplyClick = useCreateFireAnalyticsFromTrigger(triggerConfluenceCommentReplyButtonClicked);
  var fireConfluenceCommentReplyCreated = useCreateFireAnalyticsFromTrigger(triggerConfluenceCommentReplyCreated);
  useEffect(function () {
    if (isRequestNotificationType) {
      fireRequestNotificationViewed();
    }
  }, [isRequestNotificationType, fireRequestNotificationViewed]);
  var ref = useRef(null);
  var _useFocusManager = useFocusManager(ref, listIndex),
    focused = _useFocusManager.focused,
    handleKeydown = _useFocusManager.handleKeydown,
    handleOnFocus = _useFocusManager.handleOnFocus,
    handleOnBlur = _useFocusManager.handleOnBlur;
  useFocus(ref, focused);
  var pathText = buildPathText(notification.content.path, notification.content.entity);
  var pathUrl = getPathUrl(notification.content.entity, notification.content.path);
  var handleToggleReadState = function handleToggleReadState() {
    var toState = notification.readState === RenderableReadState.READ ? MarkRequestReadState.UNREAD : MarkRequestReadState.READ;
    fireMarkOneReadClicked(toState, notification, listIndex);
    markBulkNotifications([notification], toState);
  };
  var toggleExpanded = function toggleExpanded() {
    var newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);
    if (newIsExpanded) {
      fireTriggerDocumentExpanded(notification, listIndex);
    } else {
      fireTriggerDocumentCollapsed(notification, listIndex);
    }
  };
  var handleClick = useCallback(function (linkType, clickType, event) {
    var result = onClick === null || onClick === void 0 ? void 0 : onClick(linkType, clickType, event);
    var isUnread = notification.readState === RenderableReadState.UNREAD;
    var shouldMarkAsRead = function shouldMarkAsRead() {
      if ((result === null || result === void 0 ? void 0 : result.shouldMarkAsRead) === false) {
        return false;
      }
      return clickType === 'left' || (result === null || result === void 0 ? void 0 : result.shouldMarkAsRead);
    };
    if (isUnread && shouldMarkAsRead()) {
      markBulkNotifications([notification], MarkRequestReadState.READ);
    }
    fireNotificationLinkClicked(notification, listIndex, linkType, clickType);
  }, [fireNotificationLinkClicked, listIndex, markBulkNotifications, notification, onClick]);
  var getMainActionLabel = function getMainActionLabel() {
    if (!notification.content.actions || notification.content.actions.length === 0) {
      return notification.content.message;
    }
    return notification.content.actions[0].title;
  };
  var mainActionUrl = getUrlWithAtlOrigin(getMainActionUrl(notification.content.actions, notification.content.entity), originId);
  var expectedBodyItemCount = notification.bodyItemCount;
  var actualBodyItemCount = (_notification$content4 = (_notification$content5 = notification.content) === null || _notification$content5 === void 0 || (_notification$content5 = _notification$content5.body) === null || _notification$content5 === void 0 ? void 0 : _notification$content5.items.length) !== null && _notification$content4 !== void 0 ? _notification$content4 : 0;
  var bodyItemCount = notification.loadingState === LoadingState.COMPLETE ? actualBodyItemCount : expectedBodyItemCount;
  var onKeyDownHandler = function onKeyDownHandler(event) {
    setHasUserInteracted(true);
    switch (event.key) {
      case 'r':
        {
          fireTriggerKeyboardShortcutUsed('markNotificationRead', notification, listIndex);
          handleToggleReadState();
          return;
        }
      case 'e':
        {
          fireTriggerKeyboardShortcutUsed('expandNotification', notification, listIndex);
          toggleExpanded();
          return;
        }
      default:
        {
          var shortCutActionType = keyboardEventToAction(event);
          if (shortCutActionType) {
            event.preventDefault(); // stop scroll on keydown events
            fireTriggerKeyboardShortcutUsed(KeyboardActionToAnalyticSubject(shortCutActionType), notification, listIndex);
          }
          handleKeydown(event);
        }
    }
  };
  var timestamp = selectUnit(new Date(notification.timestamp), Date.now());
  var timestampText = intl.formatRelativeTime(timestamp.value, timestamp.unit);
  var handleUserInteraction = function handleUserInteraction() {
    setHasUserInteracted(true);
  };
  var formattedTextValues = useFormattedTextValues(notification.content.templateVariables, notification.cloudId);
  var handleMainActionClick = function handleMainActionClick(clickType) {
    return function (event) {
      if (mainActionUrl) {
        handleClick(LinkType.MAIN_ACTION, clickType, event);
      }
    };
  };
  var renderReadStateIndicatorDropdownMenu = function renderReadStateIndicatorDropdownMenu() {
    return /*#__PURE__*/React.createElement(ReadStateDropdownMenu, {
      triggerComponent: ReadStateIndicator,
      triggerComponentProps: {
        handleActiveChange: setMarkAsReadActive,
        read: notification.readState === RenderableReadState.READ,
        testId: 'read-state-indicator',
        onToggle: handleToggleReadState,
        groupReadStates: positionInGroup ? undefined : groupReadStates
      },
      onToggle: handleToggleReadState,
      onGroupToggle: onGroupReadStateChange,
      groupReadStates: groupReadStates
    });
  };
  var renderReadStateIndicatorButton = function renderReadStateIndicatorButton() {
    return /*#__PURE__*/React.createElement(ReadStateIndicator, {
      handleActiveChange: setMarkAsReadActive,
      read: notification.readState === RenderableReadState.READ,
      onToggle: handleToggleReadState,
      testId: "read-state-indicator"
    });
  };
  var renderInboxStyleReadStateIndicator = function renderInboxStyleReadStateIndicator() {
    return /*#__PURE__*/React.createElement("div", {
      ref: inboxStyleReadStateIndicatorRef
    }, displayReadStateContextMenu ? renderReadStateIndicatorDropdownMenu() : /*#__PURE__*/React.createElement(InboxReadStateIndicatorContainer, null, renderReadStateIndicatorButton()));
  };
  var renderStandardStyleReadStateIndicator = function renderStandardStyleReadStateIndicator() {
    return displayReadStateContextMenu ? renderReadStateIndicatorDropdownMenu() : renderReadStateIndicatorButton();
  };
  var useAttachmentsFooterFlagEnabled = FeatureGates.checkGate('notification-log_enable_attachments-space');
  var attachmentsRenderStyle = useAttachmentsFooterFlagEnabled ? 'attachmentsFooter' : 'inlinePlainTextPlaceholder';
  var isComment = mainActionUrl ? new URL(mainActionUrl).searchParams.has('focusedCommentId') : false;
  var commentId = isComment && mainActionUrl ? new URL(mainActionUrl).searchParams.get('focusedCommentId') : '';
  var bellNotifRepliesAndReactionsEnabled = false;
  if (isComment && commentId && notification.analyticsAttributes.registrationProduct === 'confluence' && CONFLUENCE_REACT_REPLY_BAR_EVENTS.includes(notification.analyticsAttributes.registrationName) && actualBodyItemCount !== 0 && mainActionUrl && emojiProvider && isEligibleForBellNotifRepliesCtxExp) {
    bellNotifRepliesAndReactionsEnabled = FeatureGates.checkGate('cc-notifications-react-reply-from-notif-draw');
  }
  var isConfluenceComment = isComment && notification.analyticsAttributes.registrationProduct === 'confluence';
  var handleOnEditorShown = useCallback(function (containerId, containerType, isInlineComment, replyCommentParentId, currentUserIsWatching) {
    fireConfluenceCommentReplyClick(notification, listIndex);
    setContainerId(containerId);
    setContainerType(containerType);
    setIsInlineComment(isInlineComment);
    setReplyCommentParentId(replyCommentParentId);
    setCurrentUserIsWatching(currentUserIsWatching);
    setShowCommentEditor(true);
  }, [notification, listIndex, fireConfluenceCommentReplyClick]);
  var handleJiraReplyButtonClick = useCallback(function (replyCommentParentId, issueAri, projectId, projectType) {
    var _issueAri$split$pop;
    // Generate new CommentSessionId when comment starts
    var commentSessionId = generateCommentSessionId();
    setJiraReplyAnalyticsPayload({
      commentSessionId: commentSessionId,
      issueId: ((_issueAri$split$pop = issueAri.split(':').pop()) === null || _issueAri$split$pop === void 0 ? void 0 : _issueAri$split$pop.split('/').pop()) || '',
      projectType: (projectType === null || projectType === void 0 ? void 0 : projectType.toLowerCase()) || '',
      projectId: projectId || ''
    });
    fireJiraCommentReplyClick(notification, listIndex, commentSessionId);
    setReplyCommentParentId(replyCommentParentId);
    setShowJiraCommentEditor(true);
  }, [notification, listIndex, fireJiraCommentReplyClick]);
  var onCommentSave = useCallback(function (replyCommentId, replyCommentUrlString, commentType) {
    fireConfluenceCommentReplyCreated(notification, listIndex, replyCommentId, commentType, containerId, containerType, commentId);
    onCommentCreatedFn === null || onCommentCreatedFn === void 0 || onCommentCreatedFn(replyCommentId, replyCommentUrlString, containerType);
    setShowCommentThreadLink(true);
    setShowCommentEditor(false);
    var isUnread = notification.readState === RenderableReadState.UNREAD;
    if (isUnread) {
      markBulkNotifications([notification], MarkRequestReadState.READ);
    }
  }, [notification, listIndex, fireConfluenceCommentReplyCreated, containerId, containerType, commentId, markBulkNotifications, onCommentCreatedFn]);
  var onCommentSaveJira = useCallback(function (replyCommentId, replyCommentUrlString, commentType, mentionedUserIds) {
    var _jiraReplyAnalyticsPa, _jiraReplyAnalyticsPa2, _jiraReplyAnalyticsPa3, _jiraReplyAnalyticsPa4;
    fireJiraCommentReplyCreated(notification, listIndex, replyCommentId, replyCommentParentId, (_jiraReplyAnalyticsPa = jiraReplyAnalyticsPayload.issueId) !== null && _jiraReplyAnalyticsPa !== void 0 ? _jiraReplyAnalyticsPa : '', 'issue', (_jiraReplyAnalyticsPa2 = jiraReplyAnalyticsPayload.commentSessionId) !== null && _jiraReplyAnalyticsPa2 !== void 0 ? _jiraReplyAnalyticsPa2 : '', (_jiraReplyAnalyticsPa3 = jiraReplyAnalyticsPayload.projectType) !== null && _jiraReplyAnalyticsPa3 !== void 0 ? _jiraReplyAnalyticsPa3 : '', (_jiraReplyAnalyticsPa4 = jiraReplyAnalyticsPayload.projectId) !== null && _jiraReplyAnalyticsPa4 !== void 0 ? _jiraReplyAnalyticsPa4 : '', mentionedUserIds);
    setShowJiraCommentEditor(false);

    // Reset CommentSessionId when comment is successfully created
    setJiraReplyAnalyticsPayload({});

    // Add toast
    var id = Date.now();
    setFlags([{
      id: id,
      link: replyCommentUrlString
    }]);
    var isUnread = notification.readState === RenderableReadState.UNREAD;
    if (isUnread) {
      markBulkNotifications([notification], MarkRequestReadState.READ);
    }
  }, [notification, listIndex, fireJiraCommentReplyCreated, replyCommentParentId, markBulkNotifications, jiraReplyAnalyticsPayload]);
  var onCommenteditorClose = useCallback(function () {
    setShowCommentEditor(false);
  }, []);
  var onJiraReplyBoxClose = useCallback(function () {
    var _jiraReplyAnalyticsPa5;
    fireJiraCommentReplyCancelled(notification, listIndex, (_jiraReplyAnalyticsPa5 = jiraReplyAnalyticsPayload.commentSessionId) !== null && _jiraReplyAnalyticsPa5 !== void 0 ? _jiraReplyAnalyticsPa5 : '');
    setShowJiraCommentEditor(false);
    // Reset CommentSessionId when reply is cancelled
    setJiraReplyAnalyticsPayload({});
  }, [notification, listIndex, fireJiraCommentReplyCancelled, jiraReplyAnalyticsPayload]);
  var getPositionInGroupForNotification = function getPositionInGroupForNotification() {
    if (showCommentEditor || showJiraCommentEditor) {
      if (!positionInGroup) {
        return PositionInGroup.FIRST;
      } else if (positionInGroup === PositionInGroup.LAST) {
        return PositionInGroup.MIDDLE;
      }
    }
    return positionInGroup;
  };
  var calculateLeftSpacing = function calculateLeftSpacing() {
    var _profileWrapperRef$cu, _profileWrapperRef$cu2, _inboxStyleReadStateI, _inboxStyleReadStateI2;
    var width1 = (_profileWrapperRef$cu = (_profileWrapperRef$cu2 = profileWrapperRef.current) === null || _profileWrapperRef$cu2 === void 0 ? void 0 : _profileWrapperRef$cu2.clientWidth) !== null && _profileWrapperRef$cu !== void 0 ? _profileWrapperRef$cu : 0;
    var width2 = (_inboxStyleReadStateI = (_inboxStyleReadStateI2 = inboxStyleReadStateIndicatorRef.current) === null || _inboxStyleReadStateI2 === void 0 ? void 0 : _inboxStyleReadStateI2.clientWidth) !== null && _inboxStyleReadStateI !== void 0 ? _inboxStyleReadStateI : 0;
    return width1 + width2;
  };
  var handleMarkAsRead = useCallback(function () {
    var isUnread = notification.readState === RenderableReadState.UNREAD;
    if (isUnread) {
      markBulkNotifications([notification], MarkRequestReadState.READ);
    }
  }, [notification, markBulkNotifications]);
  var _useContext = useContext(NotificationsStoreContext),
    _useContext$userId = _useContext.userId,
    userId = _useContext$userId === void 0 ? '' : _useContext$userId;
  var reactReplyBarExperienceTracking = {
    notification: notification,
    listIndex: listIndex,
    userId: userId
  };
  return /*#__PURE__*/React.createElement(RelayEnvironmentProvider, null, /*#__PURE__*/React.createElement(BackgroundFadeOut, {
    shouldFadeOut: notification.isUnseen
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: className
  }, /*#__PURE__*/React.createElement(ViewedTracker, {
    createFireAnalyticsEvent: function createFireAnalyticsEvent(viewedDuration) {
      return fireNotificationViewedTrackEvent(notification, listIndex, viewedDuration);
    },
    hasUserInteracted: hasUserInteracted
  }), /*#__PURE__*/React.createElement(ItemContainer, {
    "data-testid": "notification-item-container",
    "aria-describedby": "".concat(notification.id, "_summary"),
    "aria-posinset": listIndex,
    "aria-setsize": -1 // -1 As the total list size in indeterminate
    ,
    onMouseDown: handleUserInteraction,
    onTouchStart: handleUserInteraction,
    backgroundOnActive:
    // Prevent a notification focussing on click if there is no action
    (notification.readState === RenderableReadState.UNREAD || ((_notification$content6 = notification.content.entity) === null || _notification$content6 === void 0 ? void 0 : _notification$content6.link) !== undefined) && !markAsReadActive
  }, /*#__PURE__*/React.createElement(ExperienceSuccess, {
    name: Experiences.RENDER_NOTIFICATIONS_WITHOUT_CONTENT,
    attributes: {
      noNotificationsScreen: false
    }
  }), /*#__PURE__*/React.createElement(ExperienceMark, {
    mark: NotificationExperienceMarkKeys.NOTIFICATION_LIGHTWEIGHT_RENDER,
    phase: "end"
  }), /*#__PURE__*/React.createElement(MainAction, {
    ref: ref,
    onKeyDown: onKeyDownHandler,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    href: mainActionUrl,
    onClick: handleMainActionClick('left'),
    onAuxClick: handleMainActionClick('middle'),
    onContextMenu: handleMainActionClick('right'),
    "data-testid": "notification-item-main-action",
    "aria-label": "".concat(getMainActionLabel(), " ").concat(timestampText)
  }), /*#__PURE__*/React.createElement(NotificationContainer, null, readStateIndicatorStyle === 'inbox' && renderInboxStyleReadStateIndicator(), /*#__PURE__*/React.createElement(EnableMouseEventsProfileWrapper, {
    ref: profileWrapperRef
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallbackUI: /*#__PURE__*/React.createElement(AvatarSkeleton, null),
    subjectId: "avatarContainer"
  }, !isRequestNotificationType && !isDesNotificationType ? positionInGroup || showCommentEditor || showJiraCommentEditor ? /*#__PURE__*/React.createElement(AvatarThread, {
    positionInGroup: getPositionInGroupForNotification()
  }, /*#__PURE__*/React.createElement(AvatarContainer, {
    actors: notification.content.actors
  })) : /*#__PURE__*/React.createElement(AvatarContainer, {
    actors: notification.content.actors
  }) : /*#__PURE__*/React.createElement(AtlassianAvatarContainer, null))), /*#__PURE__*/React.createElement(FullWidth, null, /*#__PURE__*/React.createElement(FlexRow, null, /*#__PURE__*/React.createElement(SummaryContainer, null, /*#__PURE__*/React.createElement(MessageWithTimestamp, {
    id: "".concat(notification.id, "_summary"),
    message: /*#__PURE__*/React.createElement(FormattedText, {
      text: notification.content.message,
      values: formattedTextValues
    }),
    timestamp: timestampText
  }), (notification === null || notification === void 0 || (_notification$content7 = notification.content) === null || _notification$content7 === void 0 ? void 0 : _notification$content7.entity) && (positionInGroup === undefined || positionInGroup === PositionInGroup.FIRST) && /*#__PURE__*/React.createElement(Entity, {
    text: /*#__PURE__*/React.createElement(FormattedText, {
      text: notification.content.entity.title,
      values: formattedTextValues
    }),
    entity: notification.content.entity,
    onClick: handleClick
  }), ((_notification$content8 = notification.content) === null || _notification$content8 === void 0 ? void 0 : _notification$content8.path.length) > 0 && (positionInGroup === undefined || positionInGroup === PositionInGroup.FIRST) && /*#__PURE__*/React.createElement(Path, {
    url: getUrlWithAtlOrigin(pathUrl, originId),
    text: /*#__PURE__*/React.createElement(FormattedText, {
      text: pathText,
      values: formattedTextValues
    }),
    onClick: handleClick
  })), readStateIndicatorStyle === 'standard' && renderStandardStyleReadStateIndicator()), bodyItemCount > 0 && /*#__PURE__*/React.createElement(EnableMouseEventsDocumentWrapper, null, /*#__PURE__*/React.createElement(Document, {
    onToggleExpand: toggleExpanded,
    loadingState: forceContentLoading ? LoadingState.LOADING : notification.loadingState,
    notification: notification,
    notificationPosition: listIndex,
    isExpanded: isExpanded,
    cloudId: notification.cloudId,
    formattedTextValues: formattedTextValues,
    attachmentsRenderStyle: attachmentsRenderStyle,
    mainActionUrl: mainActionUrl,
    onDocumentClick: handleClick,
    isConfluenceComment: isConfluenceComment
  })), bellNotifRepliesAndReactionsEnabled && mainActionUrl && notification.cloudId && commentId && emojiProvider && /*#__PURE__*/React.createElement(EnableMouseEventsReactReplyWrapper, null, /*#__PURE__*/React.createElement(ReactReplyBar, {
    experienceTrackingAttributes: reactReplyBarExperienceTracking,
    contentLink: mainActionUrl,
    commentId: commentId,
    cloudId: notification.cloudId,
    emojiProvider: emojiProvider,
    isEditorShown: showCommentEditor,
    onCommentEditorShown: handleOnEditorShown,
    onOpenFullThreadLinkClick: handleClick,
    showFullThreadLink: showCommentThreadLink,
    handleMarkAsRead: handleMarkAsRead
  })), commentId && JIRA_REACT_BAR_EVENTS.includes(notification.analyticsAttributes.registrationName) && actualBodyItemCount !== 0 && mainActionUrl && emojiProvider && notification.cloudId && (FeatureGates.getExperimentValue('inline-reply-comment-notif-exp', 'isEnabled', false) ? /*#__PURE__*/React.createElement(EnableMouseEventsReactReplyWrapper, null, /*#__PURE__*/React.createElement(ErrorBoundarySuspense, {
    subjectId: "jiraReactReplyBar",
    loadingFallback: null,
    errorFallback: /*#__PURE__*/React.createElement(React.Fragment, null),
    onErrorCallback: function onErrorCallback() {
      setShowJiraCommentEditor(false);
    }
  }, /*#__PURE__*/React.createElement(JiraReactReplyBar, {
    "data-testid": "jira-react-reply-bar",
    contentLink: mainActionUrl,
    commentId: commentId,
    cloudId: notification.cloudId,
    emojiProvider: emojiProvider,
    experienceTrackingAttributes: reactReplyBarExperienceTracking,
    handleMarkAsRead: handleMarkAsRead,
    isEditorShown: showJiraCommentEditor,
    onCommentEditorShown: handleJiraReplyButtonClick,
    onOpenFullThreadLinkClick: handleClick,
    subProduct: subProduct
  }))) : FeatureGates.getExperimentValue('jira-comment-reactions-from-notification-draw', 'isEnabled', false) && /*#__PURE__*/React.createElement(EnableMouseEventsReactReplyWrapper, null, /*#__PURE__*/React.createElement(JiraReactBar, {
    contentLink: mainActionUrl,
    commentId: commentId,
    cloudId: notification.cloudId,
    emojiProvider: emojiProvider,
    experienceTrackingAttributes: reactReplyBarExperienceTracking,
    handleMarkAsRead: handleMarkAsRead
  }))))), showCommentEditor && mainActionUrl && notification.cloudId && /*#__PURE__*/React.createElement(Box, {
    paddingBlockStart: "space.200"
  }, /*#__PURE__*/React.createElement(ErrorBoundarySuspense, {
    subjectId: "notificationsCommentEditor",
    loadingFallback: null,
    errorFallback: /*#__PURE__*/React.createElement(React.Fragment, null),
    onErrorCallback: function onErrorCallback() {
      setShowCommentEditor(false);
    }
  }, /*#__PURE__*/React.createElement(LazyCommentEditor, {
    onSaveReply: onCommentSave,
    onCancelReply: onCommenteditorClose,
    positionInGroup: positionInGroup,
    emojiProvider: emojiProvider,
    mentionProvider: mentionProvider,
    parentCommentId: replyCommentParentId,
    notificationUrlString: mainActionUrl,
    notificationCloudId: notification.cloudId,
    containerId: containerId,
    containerType: containerType,
    isInlineComment: isInlineComment,
    currentUserIsWatching: currentUserIsWatching,
    onUnsavedChanges: onUnsavedCommentDraft
  }))), showJiraCommentEditor && mainActionUrl && notification.cloudId && /*#__PURE__*/React.createElement(Box, {
    paddingBlockStart: "space.200"
  }, /*#__PURE__*/React.createElement(ErrorBoundarySuspense, {
    subjectId: "notificationsJiraCommentEditor",
    loadingFallback: null,
    errorFallback: /*#__PURE__*/React.createElement(React.Fragment, null),
    onErrorCallback: function onErrorCallback() {
      setShowJiraCommentEditor(false);
    }
  }, /*#__PURE__*/React.createElement(LazyJiraCommentEditor, {
    onSaveReply: onCommentSaveJira,
    onCancelReply: onJiraReplyBoxClose,
    positionInGroup: positionInGroup,
    emojiProvider: emojiProvider,
    parentCommentId: replyCommentParentId,
    notificationUrlString: mainActionUrl,
    notificationCloudId: notification.cloudId,
    containerId: containerId,
    containerType: containerType
  }))), /*#__PURE__*/React.createElement(FlexRow, null, /*#__PURE__*/React.createElement(DocumentLeftSpacing, {
    style: {
      width: "".concat(calculateLeftSpacing(), "px")
    }
  }), /*#__PURE__*/React.createElement(React.Fragment, null, !showAdditionalCtaButtons && expandGroupButton, showAdditionalCtaButtons && /*#__PURE__*/React.createElement(AdditionalCtaButtons, {
    notification: notification,
    markBulkNotifications: markBulkNotifications
  }))))), /*#__PURE__*/React.createElement(SuccessFlags, {
    flags: flags,
    onDismissed: function onDismissed() {
      return setFlags([]);
    }
  }));
}
export default NotificationItem;